import type { DocumentNode, FragmentDefinitionNode } from 'graphql';
import { isNull } from 'lodash';
import { useFragment } from '@frameio/graphql-client';
import type {
  Workspace,
  WorkspacePermissions,
} from '@frameio/graphql-client/generated';
import {
  CanCreateProjectPermissionForWorkspace,
  CanDeletePermissionForWorkspace,
  CanEditUserPermissionsPermissionForWorkspace,
  CanManageResourceControlPoliciesForWorkspace,
  CanUpdatePermissionForWorkspace,
  CanViewUserGroupPermissionForWorkspace,
  CanViewUserPermissionsPermissionForWorkspace,
} from '@frameio/graphql-client/generated';

type UseWorkspacePermissionsArgs = {
  /** The permission field to check */
  check: keyof Omit<WorkspacePermissions, '__typename'>;
  /** Whether to consider the permission true initially prior to data resolving */
  isInitiallyPermitted?: boolean;
  /** The id of the Workspace to check against */
  workspaceId: Workspace['id'];
};

/**
 * Reactive hook to read Workspace-level permissions directly off the cache
 * @param {check, isInitiallyPermitted, workspaceId}
 * @returns A boolean of whether a given check for the Workspace with the given `workspaceId` is true
 */
export const useWorkspacePermissions = ({
  check,
  isInitiallyPermitted = false,
  workspaceId,
}: UseWorkspacePermissionsArgs): boolean | null => {
  let fragment: DocumentNode;

  switch (check) {
    case 'canCreateProject':
      fragment = CanCreateProjectPermissionForWorkspace;
      break;
    case 'canDelete':
      fragment = CanDeletePermissionForWorkspace;
      break;
    case 'canEditUserPermissions':
      fragment = CanEditUserPermissionsPermissionForWorkspace;
      break;
    case 'canUpdate':
      fragment = CanUpdatePermissionForWorkspace;
      break;
    case 'canViewUserGroup':
      fragment = CanViewUserGroupPermissionForWorkspace;
      break;
    case 'canViewUserPermissions':
      fragment = CanViewUserPermissionsPermissionForWorkspace;
      break;
    case 'canManageResourceControlPolicies':
      fragment = CanManageResourceControlPoliciesForWorkspace;
      break;
    default:
      break;
  }

  const fragmentNode = fragment.definitions[0] as FragmentDefinitionNode;

  const result = useFragment({
    fragment,
    fragmentName: fragmentNode.name.value,
    from: {
      __typename: 'Workspace',
      id: workspaceId ?? '',
    },
  });

  // If the permission does not exist yet, we want to check against that rather than defaulting to false
  const isPermissionUnresolved =
    isNull(result) ||
    Object.keys(result?.data)?.length < 1 ||
    !result?.complete;

  /**
   * Return the result of the fragment, and fall back to the prop that controls whether we consider the permission true
   * prior to the permission being resolved
   */
  return isPermissionUnresolved
    ? isInitiallyPermitted
    : result.data?.permissions?.[check];
};
